/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { useNavigate, useParams } from "react-router-dom";
import ListView from "../../components/common/ListView";
import NoDataFound from "../../components/common/NoDataFound";
import formatDate, {
  formatDateTime,
  formatDateWithTime,
} from "../../utilities/helper";
import { useFetchLinehaulManifestById } from "../../apis/pickupApis";
import {
  IconChevronDown,
  IconChevronLeft,
  IconChevronUp,
  IconCloudDown,
  IconDownload,
} from "@tabler/icons-react";
import Pagination from "../../components/common/Pagination";
import { useEffect, useState } from "react";
import TimelineUI from "./_components/TimelineUI";

interface DocumentLinkProps {
  value?: string;
  docUrl?: string;
  label: string;
}

const DocumentLink = ({ value, docUrl, label }: DocumentLinkProps) => {
  if (!value && !docUrl) return null;

  return (
    <div className="flex items-center gap-2">
      {value && <span className="font-medium text-gray-800">{value}</span>}
      {docUrl && (
        <a
          href={docUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-cente hover:bg-[#FBD037]/20 p-0.5 px-1 rounded"
          title={`View ${label} Document`}
          aria-label={`View ${label} Document`}
        >
          <IconCloudDown strokeWidth={1.5} size={22} />
        </a>
      )}
    </div>
  );
};

const Section = ({
  title,
  children,
  className,
  mainClassName,
}: {
  title?: string;
  className?: string;
  mainClassName?: string;
  children: React.ReactNode;
}) => (
  <div className="bg-white shadow-sm border border-gray-200 rounded-2xl p-4 sm:p-5 space-y-4">
    {children}
  </div>
);

const StatCard = ({
  label,
  value,
  color,
}: {
  label: string;
  value: any;
  color: string;
}) => (
  <div
    className={`rounded-xl p-3 text-white shadow-sm flex items-center space-x-2 ${color}`}
  >
    <p className="text-xs opacity-80">{label} :</p>
    <p className="text-lg font-bold">{value || 0}</p>
  </div>
);

const InfoTile = ({ label, value }: { label: string; value: any }) => (
  <div className="bg-gray-50 hover:bg-gray-100 transition rounded-xl p-3 border  flex items-center space-x-2">
    <p className="text-xs text-gray-500">{label}</p>
    <p className="text-sm font-semibold text-gray-800 break-words">
      {value || ""}
    </p>
  </div>
);


const LinehaulViewDetails = () => {
  const TwoCol = ({
    label,
    value,
    className,
  }: {
    label: string;
    value: React.ReactNode;
    className?: string;
  }) => (
    <div className="text-sm text-[#1E293B] flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-3 border-b py-3 last:border-0 px-4">
      <p className="font-medium text-[#212121] capitalize">{label}:</p>
      <p className="sm:text-end break-words">{value || ""}</p>
    </div>
  );

  const [openSection, setOpenSection] = useState<string | null>(
    "linehaulRequestDetails",
  );
  const toggleSection = (section: string) => {
    setOpenSection((prev) => (prev === section ? null : section));
  };
  const navigate = useNavigate();
  const { id } = useParams<{ id: any }>();

  const [pageNo, setPageNo] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [scanCode, setScanCode] = useState("");

  const handlePaginationFetch = (page: number) => {
    setPageNo(page);
  };

  const {
    refetch,
    data: pickupData,
    isFetching,
    isLoading,
  } = useFetchLinehaulManifestById(id ?? "", scanCode);

  const timeLine = (pickupData?.statusHistories || [])
    .sort(
      (a: any, b: any) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    )
    .map((item: any, index: number) => ({
      id: item.id || index + 1,
      label: item.newStatus
        ?.toLowerCase()
        ?.replace(/_/g, " ")
        ?.replace(/\b\w/g, (char: string) => char.toUpperCase()),
      date: formatDateWithTime(item.createdAt),
      details: item.details || "",
    }));

  useEffect(() => {
    refetch();
  }, [pageNo, limit]);

  const statusStyles = {
    GENERATED: "bg-[#FEF1CF] border border-[#FCB910] text-[#212121]",
    ASSIGNED: "bg-[#C6DFF7] border border-[#0088FF] text-[#212121]",
    COMPLETED: "bg-[#CCEAD7] border border-[#20BF55] text-[#212121]",
    PENDING: "bg-[#FFDADA] border border-[#FF4848] text-[#212121]",
    COLLECTED: "bg-[#EEEEEE] border border-[#d2d2d2] text-[#212121]",
    RECEIVE_INBOUND: "bg-[#EEEEEE] border border-[#d2d2d2] text-[#212121]",
  } as const;
  const formattedStatus = pickupData?.status
    ? pickupData.status
        .toLowerCase()
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char: string) => char.toUpperCase())
    : "";

  const groupedContainers = pickupData?.containers?.map((container: any) => {
    const containerAwbs = container.awbs?.map((a: any) => a.awbNumber) || [];

    const matchedShipments =
      pickupData?.shipments?.filter((s: any) =>
        containerAwbs.includes(s.awbNumber),
      ) || [];
    return {
      ...container,
      shipments: matchedShipments,
    };
  });
  // Scan awbs
  useEffect(() => {
    let buffer = "";

    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      ) {
        return;
      }

      if (e.key === "Enter") {
        if (buffer.length > 0) {
          setScanCode(buffer);
          buffer = "";
        }
      } else {
        buffer += e.key;
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const displayContainers = pickupData?.scanned
    ? pickupData?.containers
    : groupedContainers;

  return (
    <>
      <div className="p-4 pt-0 space-y-4">
        {/* Header */}
        <div className="flex flex-wrap gap-3 justify-between items-center bg-white p-3 sm:p-4 rounded-xl shadow-sm border">
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(-1)}
              className="p-1.5 border border-[#D2D2D2] rounded-full hover:bg-[#F7F6F2] transition"
            >
              <IconChevronLeft strokeWidth={1.5} size={22} />
            </button>
            <div className="flex items-center gap-2">
              <h1 className="text-base sm:text-lg xl:text-xl text-[#212121] tracking-[-0.5px]  ">
                View Request: {pickupData?.customId}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span
              className={`inline-flex items-center justify-center px-4 py-1.5 text-xs font-medium rounded-full whitespace-nowrap ${
                statusStyles[pickupData?.status as keyof typeof statusStyles] ||
                "bg-[#EEEEEE] text-[#212121]"
              }`}
            >
              {formattedStatus}
            </span>
            {pickupData?.barcodeUrl && (
              <a
                href={pickupData.barcodeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs bg-[#FBD037] hover:bg-yellow-400 text-black px-4 py-1.5 rounded-full whitespace-nowrap"
              >
                <IconDownload size={16} />
                Download Barcode
              </a>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-1 gap-4 xl:gap-6 w-full">
          <TimelineUI steps={timeLine} currentStep={timeLine.length - 1} />
          <Section>
            <div
              onClick={() => toggleSection("linehaulRequestDetails")}
              className="flex justify-between items-center cursor-pointer"
            >
              <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                📦 Linehual Request Details
              </h2>

              <button className="p-2 border rounded-full hover:bg-gray-100 transition">
                {openSection === "linehaulRequestDetails" ? (
                  <IconChevronUp size={22} stroke={1.5} />
                ) : (
                  <IconChevronDown size={22} stroke={1.5} />
                )}
              </button>
            </div>

            {openSection === "linehaulRequestDetails" && (
              <div className="bg-white border rounded-2xl p-5 shadow-sm hover:shadow-md transition">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    🚚 Linehual Details
                  </h3>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
                  <InfoTile
                    label="From Warehouse"
                    value={pickupData?.fromWarehouse}
                  />

                  <InfoTile label="Created By" value={pickupData?.createdBy} />

                  <InfoTile
                    label="No. of Packages"
                    value={pickupData?.totalShipmentCount}
                  />
                  <InfoTile
                    label="Date"
                    value={formatDate(pickupData?.createdAt)}
                  />

                  {(pickupData?.status === "ASSIGNED" ||
                    pickupData?.status === "COMPLETED" ||
                    pickupData?.status === "PENDING") && (
                    <>
                      <InfoTile
                        label="Assigned Driver"
                        value={pickupData?.driverName}
                      />

                      <InfoTile
                        label="Driver Contact"
                        value={pickupData?.driverMobile}
                      />
                    </>
                  )}

                  {(pickupData?.status === "ASSIGNED" ||
                    pickupData?.status === "COMPLETED" ||
                    pickupData?.status === "PENDING") && (
                    <>
                      <InfoTile
                        label=" Staff Name"
                        value={pickupData?.staffName}
                      />
                    </>
                  )}

                  {(pickupData?.status === "COMPLETED" ||
                    pickupData?.status === "PENDING") && (
                    <>
                      <InfoTile
                        label="Warehouse"
                        value={pickupData?.toWarehouse}
                      />
                      <InfoTile
                        label="Received By"
                        value={pickupData?.assignedStaff}
                      />
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 xl:col-span-3">
                        <StatCard
                          label="Total"
                          value={pickupData?.totalShipmentCount}
                          color="bg-gray-800"
                        />
                        <StatCard
                          label="Received"
                          value={pickupData?.verifiedShipmentsCount}
                          color="bg-green-500"
                        />
                        <StatCard
                          label="Missing"
                          value={pickupData?.missingShipmentsCount}
                          color="bg-yellow-500"
                        />
                        <StatCard
                          label="Damage"
                          value={pickupData?.damageShipmentsCount}
                          color="bg-red-500"
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </Section>

          {/* <Section>
            <div
              onClick={() => toggleSection("linehaulRequestDetails")}
              className="flex justify-between items-center cursor-pointer"
            >
              <h2 className="text-lg font-semibold mb-4 text-gray-800">
                Linehual Request Details
              </h2>
              <button className="p-2 border rounded-full hover:bg-gray-100 transition">
                {openSection === "linehaulRequestDetails" ? (
                  <IconChevronUp size={22} stroke={1.5} color="#6B7280" />
                ) : (
                  <IconChevronDown size={22} stroke={1.5} color="#6B7280" />
                )}
              </button>
            </div>
            {openSection === "linehaulRequestDetails" && (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
                <div className="bg-[#FCFDF8] border rounded-2xl overflow-hidden">
                  <h3 className="text-[#212121] font-medium text-base capitalize border-b border-gray-300 border-dashed py-2 w-full bg-[#F7F6F2] rounded-t-xl px-4">
                    Sender Information
                  </h3>
                  <TwoCol
                    label="From Warehouse"
                    value={pickupData?.fromWarehouse}
                  />
                  <TwoCol
                    label="To Wharehouse"
                    value={pickupData?.toWarehouse}
                  />
                  <TwoCol label="Created By" value={pickupData?.createdBy} />
                </div>

                <div className="bg-[#FCFDF8] border rounded-2xl overflow-hidden">
                  <h3 className="text-[#212121] font-medium text-base capitalize border-b border-gray-300 border-dashed py-2 w-full bg-[#F7F6F2] rounded-t-xl px-4">
                    Shipment Details
                  </h3>
                  <TwoCol
                    className="col-span-1"
                    label="No. of Packages"
                    value={pickupData?.totalShipmentCount}
                  /> 
                  <TwoCol
                    label="Date"
                    value={formatDate(pickupData?.createdAt)}
                  />
                </div>

                {(pickupData?.status === "ASSIGNED" ||
                  pickupData?.status === "COMPLETED" ||
                  pickupData?.status === "PENDING") && (
                  <div className="bg-[#FCFDF8] border rounded-2xl overflow-hidden">
                    <h3 className="text-[#212121] font-medium text-base capitalize border-b border-gray-300 border-dashed py-2 w-full bg-[#F7F6F2] rounded-t-xl px-4">
                      Driver Details
                    </h3>
                    <TwoCol
                      label="Company"
                      value={pickupData?.assignedCompanyName}
                    />

                    <TwoCol
                      label=" Driver License"
                      value={
                        <DocumentLink
                          value={"Preview"}
                          docUrl={pickupData?.driverLicense}
                          label="Iqama"
                        />
                      }
                    />

                    <TwoCol
                      label=" Driver Iqama"
                      value={
                        <DocumentLink
                          value={"Preview"}
                          docUrl={pickupData?.driverIqmaDoc}
                          label="Iqama"
                        />
                      }
                    />

                    <TwoCol
                      label="Assigned Driver"
                      value={pickupData?.driverName}
                    />
                    <TwoCol label="Vehicle" value={pickupData?.vehicleNumber} />
                    <TwoCol label="Contact" value={pickupData?.driverMobile} />
                  </div>
                )}

                {(pickupData?.status === "ASSIGNED" ||
                  pickupData?.status === "COMPLETED" ||
                  pickupData?.status === "PENDING") && (
                  <div className="bg-[#FCFDF8] border rounded-2xl overflow-hidden">
                    <h3 className="text-[#212121] font-medium text-base capitalize border-b border-gray-300 border-dashed py-2 w-full bg-[#F7F6F2] rounded-t-xl px-4">
                      Staff Details
                    </h3>

                    <TwoCol label=" Staff Name" value={pickupData?.staffName} />

                    <TwoCol
                      label="Staff Iqama"
                      value={pickupData?.staffIqamaNo}
                    />

                    <TwoCol
                      label="Staff Email"
                      value={pickupData?.staffEmail}
                    />
                    <TwoCol
                      label="Staff Mobile"
                      value={pickupData?.staffPhone}
                    /> 
                  </div>
                )}

                {(pickupData?.status === "COMPLETED" ||
                  pickupData?.status === "PENDING") && (
                  <div className="bg-[#FCFDF8] border rounded-2xl overflow-hidden ">
                    <h3 className="text-[#212121] font-medium text-base capitalize border-b border-gray-300 border-dashed py-2 w-full bg-[#F7F6F2] rounded-t-xl px-4">
                      RIW Details
                    </h3>
                    <TwoCol
                      label="Warehouse Name"
                      value={pickupData?.toWarehouse}
                    />
                    <TwoCol
                      label="Received By"
                      value={pickupData?.assignedStaff}
                    />

                    <TwoCol
                      label="Numbers of Shipments"
                      value={pickupData?.totalShipmentCount}
                    />
                    <TwoCol
                      label="Received"
                      value={pickupData?.verifiedShipmentsCount}
                    />
                    <TwoCol
                      label="Missing"
                      value={pickupData?.missingShipmentsCount}
                    />
                    <TwoCol
                      label="Damage"
                      value={pickupData?.damageShipmentsCount}
                    />
                  </div>
                )}
              </div>
            )}
          </Section> */}

          <Section>
            <div
              onClick={() => toggleSection("linehaulShipmentList")}
              className="flex justify-between items-center col-span-2 pb-3"
            >
              <h3 className="text-[#212121] font-medium text-lg ">
                Linehual Shipment Lists
              </h3>
              <button className="p-2 border rounded-full hover:bg-gray-100 transition">
                {openSection === "linehaulShipmentList" ? (
                  <IconChevronUp size={22} stroke={1.5} color="#6B7280" />
                ) : (
                  <IconChevronDown size={22} stroke={1.5} color="#6B7280" />
                )}
              </button>
            </div>
            {openSection === "linehaulShipmentList" && (
              <>
                <h3 className="text-[#212121] font-medium text-base capitalize border-b border-gray-300 border-dashed py-2 w-full bg-[#F7F6F2] rounded-t-xl px-4">
                  Shipments ({pickupData?.totalShipmentCount})
                </h3>
                <div className="max-h-[600px] overflow-y-auto">
                  {displayContainers?.length > 0 ? (
                    <div className="space-y-6 p-4">
                      {displayContainers.map((container: any) => (
                        <div
                          key={container.id}
                          className="border rounded-2xl overflow-hidden"
                        >
                          {/* HEADER */}
                          <div className="flex justify-between items-center bg-[#F7F6F2] px-4 py-3 border-b sticky top-0 z-10">
                            <h3 className="text-sm font-semibold text-[#212121]">
                              {container.containerType} (
                              {container.containerCode})
                            </h3>

                            {container.barcodeUrl && (
                              <a
                                href={container.barcodeUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 text-xs bg-[#FBD037] hover:bg-yellow-400 text-black px-4 py-1.5 rounded-full"
                              >
                                <IconDownload size={16} />
                                Download Barcode
                              </a>
                            )}
                          </div>

                          {/* AWB TABLE */}
                          {container.shipments?.length > 0 ? (
                            <ListView
                              mailDivClassName="w-full cols-span-2 p-0"
                              mainDivClassName=""
                              trClassName="hover:bg-[#FBD037]/20 group"
                              tdClassName="rounded-r-none rounded-l-none"
                              data={container.shipments}
                              columns={[
                                {
                                  label: "AWB",
                                  render: (c: any) => c.awbNumber,
                                },
                                {
                                  label: "Origin",
                                  render: (c: any) => c.senderCity,
                                },
                                {
                                  label: "Destination",
                                  children: [
                                    {
                                      label: "Country",
                                      render: (c: any) => c.receiverCountry,
                                    },
                                    {
                                      label: "City",
                                      render: (c: any) => c.receiverCity,
                                    },
                                    {
                                      label: "District",
                                      render: (c: any) => c.receiverDistrict,
                                    },
                                  ],
                                },
                                {
                                  label: "Status",
                                  render: (c) => {
                                    const formattedStatus = c.status
                                      .toLowerCase()
                                      .replace(/_/g, " ")
                                      .replace(/\b\w/g, (char: string) =>
                                        char.toUpperCase(),
                                      );

                                    return (
                                      <div className="flex justify-center items-center">
                                        <button className="rounded-full px-4 py-1 text-center bg-[#EEEEEE] border">
                                          {formattedStatus}
                                        </button>
                                      </div>
                                    );
                                  },
                                },
                              ]}
                            />
                          ) : (
                            <div className="p-4 text-sm text-gray-500">
                              No shipments
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <NoDataFound />
                  )}
                </div>
                {pickupData?.totalPages > 1 && (
                  <div className="flex justify-center py-4">
                    <Pagination
                      totalPages={pickupData?.totalPages}
                      currentPage={pageNo}
                      fetchData={handlePaginationFetch}
                    />
                  </div>
                )}
              </>
            )}
          </Section>
        </div>
      </div>
    </>
  );
};

export default LinehaulViewDetails;
